import classNames from 'classnames';
import React, { CSSProperties, ReactNode, useCallback, useState } from 'react';
import { List, RowComponentProps } from 'react-window';
import { EMPTY_ROW_PROPS } from '../Utils/virtualizationConstants';
import { VirtualizedTableColumnFit } from './VirtualizedTable.interface';

export interface VirtualTableProps {
  header?: ReactNode;
  columnFit: VirtualizedTableColumnFit;
  onSizeChanged?: (width: number, height: number) => void;
  /** Component rendering a single row. Must be a function component, as react-window memoizes it. */
  row: (props: RowComponentProps) => React.ReactElement | null;
  rowCount: number;
  rowHeight: number;
  onRowsRendered?: (visibleRows: { startIndex: number; stopIndex: number }) => void;
  className?: string;
  style?: CSSProperties;
}

/**
 * The virtual table. Wraps the react-window `List` and renders a sticky header
 * above the virtualized rows.
 *
 * react-window 2 renders every row absolutely positioned inside the scroll
 * container, which the sticky header shares. Rows are pushed below the header
 * by the `top` offset on `.virtualizedTable .row`; react-window only ever sets
 * `transform`, so that CSS rule is safe.
 *
 * `role="rowgroup"` overrides the `role="list"` react-window puts on its own
 * container: the rows live inside a `role="table"` element, so list semantics
 * would break the table for screen readers.
 */
export function VirtualTable({
  row,
  header,
  columnFit,
  onSizeChanged,
  rowCount,
  rowHeight,
  onRowsRendered,
  className,
  style,
}: VirtualTableProps) {
  const [isOnTop, setIsOnTop] = useState(true);
  const [isOnBottom, setIsOnBottom] = useState(false);

  // Stable identity: react-window fires its onRowsRendered effect whenever this
  // callback changes, so an inline arrow would re-run it on every render.
  const handleRowsRendered = useCallback(
    (visibleRows: { startIndex: number; stopIndex: number }) => {
      setIsOnTop(visibleRows.startIndex === 0);
      setIsOnBottom(visibleRows.stopIndex === rowCount - 1);

      onRowsRendered?.(visibleRows);
    },
    [rowCount, onRowsRendered],
  );

  const handleResize = useCallback(
    ({ width, height }: { width: number; height: number }) => {
      onSizeChanged?.(width, height);
    },
    [onSizeChanged],
  );

  return (
    <List
      role="rowgroup"
      className={classNames(className, 'table', 'virtualizedTable', {
        fitColumns: columnFit === VirtualizedTableColumnFit.Fit,
        isOnTop: isOnTop,
        isOnBottom: isOnBottom,
      })}
      rowComponent={row}
      rowCount={rowCount}
      rowHeight={rowHeight}
      rowProps={EMPTY_ROW_PROPS}
      style={style}
      onResize={handleResize}
      onRowsRendered={handleRowsRendered}
    >
      <div role="presentation" className="sticky rowgroup headerGroup">
        {header}
      </div>
    </List>
  );
}
